
import React, { useState, useEffect, useMemo } from 'react';
import { User, MaintenanceTicket, Announcement, MonthlyPayment, ResidentUnit } from '../types';
import { supabaseService, Expense } from '../services/supabaseService';
import { 
  AlertCircle, 
  MessageCircle, 
  ShieldCheck, 
  Loader2, 
  AlertTriangle, 
  Wallet, 
  ArrowDownCircle, 
  TrendingUp 
} from 'lucide-react';

interface DashboardProps {
  user: User;
  onNavigate: (tab: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onNavigate }) => {
  const [tickets, setTickets] = useState<MaintenanceTicket[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [residents, setResidents] = useState<ResidentUnit[]>([]);
  const [payments, setPayments] = useState<MonthlyPayment[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const now = new Date();
        const [t, a, r, p, e] = await Promise.all([
          supabaseService.getTickets(),
          supabaseService.getAnnouncements(),
          supabaseService.getResidents(),
          supabaseService.getPayments(now.getMonth(), now.getFullYear()),
          supabaseService.getExpenses()
        ]);

        setTickets(t || []);
        setAnnouncements(a || []);
        setResidents(r || []);
        setPayments(p || []);
        setExpenses(e || []);
        setError(null);
      } catch (err: any) {
        const errorMsg = err.message || "Грешка при комуникација со базата";
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Пресметка на финансиски статистики за Dashboard
  const finStats = useMemo(() => {
    const totalUnits = residents.length || 0;
    const paidCount = payments.filter(p => p.isPaid).length;
    const collectionRate = totalUnits > 0 ? Math.round((paidCount / totalUnits) * 100) : 0;
    
    // Пресметка на фонд (само од потврдени уплати)
    const totalCollected = payments.filter(p => p.isPaid).reduce((acc, curr) => acc + curr.amount, 0);
    const totalSpent = expenses.reduce((acc, curr) => acc + curr.amount, 0);
    const commonFund = totalCollected - totalSpent;

    const lastExpense = expenses[0] || null;

    return { commonFund, collectionRate, lastExpense, paidCount, totalUnits };
  }, [residents, payments, expenses]);

  if (loading) return (
    <div className="flex h-96 items-center justify-center flex-col space-y-4">
      <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
      <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Се вчитува контролната табла...</p>
    </div>
  );

  const pendingTickets = tickets.filter(t => t.status !== 'RESOLVED');

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-12">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Добредојдовте, {user.name.split(' ')[0]}!</h1>
          <p className="text-slate-500 font-medium mt-1">Преглед на зградата и заедничкиот фонд.</p>
        </div>
      </header>

      {error && (
        <div className="bg-red-50 border border-red-100 p-4 rounded-2xl flex items-center space-x-3 text-red-600 animate-in slide-in-from-top duration-300">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm font-bold tracking-tight">Напомена: {error}</p>
        </div>
      )}

      {/* ФИНАНСИСКА ЛЕНТА (Резервен фонд) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-indigo-600 p-6 rounded-[2rem] text-white shadow-xl shadow-indigo-100 relative overflow-hidden group">
          <div className="absolute right-0 top-0 opacity-10 translate-x-4 -translate-y-4 group-hover:scale-110 transition-transform duration-700">
            <Wallet className="w-24 h-24" />
          </div>
          <div className="relative z-10">
            <span className="text-[9px] font-black uppercase tracking-[0.2em] opacity-70 block mb-1">Заеднички фонд</span>
            <h2 className="text-3xl font-black">{finStats.commonFund.toLocaleString()} ден.</h2>
            <div className="mt-3">
              <span className="inline-flex items-center px-2 py-0.5 bg-emerald-500/20 text-emerald-100 rounded-lg text-[9px] font-black uppercase tracking-widest border border-emerald-500/30">
                <ShieldCheck className="w-3 h-3 mr-1" /> Стабилно
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col justify-between">
          <div>
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-1">Месечна наплата</span>
            <div className="flex items-end justify-between mb-3">
              <h2 className="text-3xl font-black text-slate-900">{finStats.collectionRate}%</h2>
              <span className="text-[10px] font-bold text-slate-400">{finStats.paidCount} / {finStats.totalUnits} единици</span>
            </div>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 transition-all duration-1000" style={{ width: `${finStats.collectionRate}%` }} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
          <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-3">Последен трошок</span>
          <div className="flex items-start space-x-3">
             <div className="w-10 h-10 bg-red-50 text-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <ArrowDownCircle className="w-5 h-5" />
             </div>
             <div>
                <h3 className="font-black text-slate-900 text-base">
                  {finStats.lastExpense ? `-${finStats.lastExpense.amount.toLocaleString()} ден.` : '0 ден.'}
                </h3>
                <p className="text-[10px] font-medium text-slate-500 line-clamp-1">
                  {finStats.lastExpense ? finStats.lastExpense.description : 'Нема регистрирани трошоци'}
                </p>
             </div>
          </div>
        </div>
      </div>

      {/* Останати модули */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <button onClick={() => onNavigate('tickets')} className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-xl hover:border-indigo-200 transition-all text-left group">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-amber-50 p-3 rounded-2xl group-hover:bg-amber-100 transition-colors"><AlertCircle className="w-5 h-5 text-amber-600" /></div>
            <span className="text-2xl font-black text-slate-900">{pendingTickets.length}</span>
          </div>
          <h3 className="font-black text-slate-400 uppercase text-[9px] tracking-widest opacity-80">Активни дефекти</h3>
        </button>

        <button onClick={() => onNavigate('announcements')} className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-xl hover:border-indigo-200 transition-all text-left group">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-emerald-50 p-3 rounded-2xl group-hover:bg-emerald-100 transition-colors"><MessageCircle className="w-5 h-5 text-emerald-600" /></div>
            <span className="text-2xl font-black text-slate-900">{announcements.length}</span>
          </div>
          <h3 className="font-black text-slate-400 uppercase text-[9px] tracking-widest opacity-80">Известувања</h3>
        </button>

        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm text-left group">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-indigo-50 p-3 rounded-2xl"><ShieldCheck className="w-5 h-5 text-indigo-600" /></div>
            <span className="text-2xl font-black text-slate-900 tracking-tighter">А1-05</span>
          </div>
          <h3 className="font-black text-slate-400 uppercase text-[9px] tracking-widest opacity-80">Идентификатор</h3>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm">
          <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
            <h2 className="font-black text-slate-900 uppercase text-[10px] tracking-[0.2em]">Последни новости</h2>
          </div>
          <div className="divide-y divide-slate-50">
            {announcements.length > 0 ? announcements.slice(0, 3).map(ann => (
              <div key={ann.id} className="p-6 hover:bg-slate-50 transition-colors cursor-pointer group" onClick={() => onNavigate('announcements')}>
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-[9px] font-black text-indigo-600 uppercase tracking-widest">{ann.category}</span>
                  <span className="text-slate-300 text-[9px]">•</span>
                  <span className="text-[9px] font-bold text-slate-400">{ann.date}</span>
                </div>
                <h3 className="font-black text-slate-900 text-base group-hover:text-indigo-600 transition-colors leading-tight">{ann.title}</h3>
                <p className="text-sm text-slate-500 font-medium line-clamp-1 mt-1">{ann.content}</p>
              </div>
            )) : (
              <div className="p-12 text-center">
                 <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Нема активни известувања.</p>
              </div>
            )}
          </div>
        </section>

        <section className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm">
          <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
            <h2 className="font-black text-slate-900 uppercase text-[10px] tracking-[0.2em]">Дефекти во тек</h2>
          </div>
          <div className="p-6 space-y-3">
            {pendingTickets.length > 0 ? pendingTickets.slice(0, 3).map(ticket => (
              <div key={ticket.id} className="flex items-center justify-between bg-slate-50 p-4 rounded-2xl border border-slate-100 hover:border-indigo-100 transition-all cursor-pointer" onClick={() => onNavigate('tickets')}>
                <div>
                  <span className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Стан {ticket.apartment}</span>
                  <span className="font-bold text-slate-800 text-sm leading-tight block">{ticket.issue}</span>
                </div>
                <span className={`text-[9px] font-black px-2 py-1 rounded-lg uppercase tracking-widest ${
                  ticket.status === 'PENDING' ? 'bg-amber-50 text-amber-600 border border-amber-100' : 'bg-indigo-50 text-indigo-600 border border-indigo-100'
                }`}>
                  {ticket.status === 'PENDING' ? 'Чека' : 'Во тек'}
                </span>
              </div>
            )) : (
              <div className="p-12 text-center">
                 <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Сите дефекти се решени.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
